"use client"

import * as React from "react"
import { Link, useNavigate } from "react-router-dom"
import { BarChart, BookOpen, LayoutDashboard, Menu, Plus, Shield, User, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ThemeToggle } from "@/components/theme-toggle"
import { NewConversationButton } from "@/components/conversations"

export default function Navbar() {
  const [isOpen, setIsOpen] = React.useState(false)
  const navigate = useNavigate()

  const handleNewConversation = (conversation: { id: string }) => {
    navigate(`/pa/chat/${conversation.id}`)
    setIsOpen(false)
  }

  return (
    <nav role="navigation" className="sticky top-0 z-50 w-full border-b border-slate-700 bg-slate-900/98 backdrop-blur supports-[backdrop-filter]:bg-slate-900/95">
      <div className="flex h-16 items-center justify-between p-4">
        {/* Logo/Brand */}
        <div className="flex items-center space-x-2">
          <Link to="/" className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">A</span>
            </div>
            <span className="font-bold text-xl text-white">VEDAVIVI</span>
          </Link>
        </div>

        {/* Hamburger Menu - Positioned to far right */}
        <div>
          <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10 text-white hover:text-slate-300">
                {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                <span className="sr-only">Toggle menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <NewConversationButton variant="menu-item" onSuccess={handleNewConversation} />
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/pa/dashboard" className="flex items-center">
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  <span>Dashboard</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/profile" className="flex items-center">
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/learnings" className="flex items-center">
                  <BookOpen className="mr-2 h-4 w-4" />
                  <span>Learnings</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/sa" className="flex items-center">
                  <Shield className="mr-2 h-4 w-4" />
                  <span>SA Dashboard</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/sa/create-internal" className="flex items-center">
                  <Plus className="mr-2 h-4 w-4" />
                  <span>Create Internal Agent</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/sa/submit-external" className="flex items-center">
                  <Plus className="mr-2 h-4 w-4" />
                  <span>Submit External Agent</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/sa/analytics" className="flex items-center">
                  <BarChart className="mr-2 h-4 w-4" />
                  <span>SA Analytics</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1">
                <ThemeToggle />
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </nav>
  )
}